/* eslint-disable no-throw-literal */
import gql from 'graphql-tag';
import apolloClient from '../../../../utils/apolloClient';

// ─── GraphQL Query ────────────────────────────────────────────────────────────

const COMPANY_INVOICE_SUMMARY = gql`
  query CompanyInvoiceSummary($input: SearchInput) {
    company_invoice_summary(input: $input) {
      total_invoices
      this_week_invoices
      total_sale_value
      net_payout
      application_fee
      kravein_fee
      delivery_fee
      kravein_charges
      credit_card_fees
      kravein_payout
      error {
        code
        description
      }
    }
  }
`;

// ─── Runtime Validators ───────────────────────────────────────────────────────

const assertNumber = (val, fieldName) => {
  if (val === null || val === undefined || typeof val !== 'number') {
    console.warn(
      `[companyInvoiceSummaryService] Unexpected value for "${fieldName}": ${val}, defaulting to 0`,
    );
    return 0;
  }
  return val;
};

// ─── Pure Transformer ─────────────────────────────────────────────────────────

const toCompanyInvoiceSummary = raw => ({
  totalInvoices: assertNumber(raw?.total_invoices, 'total_invoices'),
  thisWeekInvoices: assertNumber(raw?.this_week_invoices, 'this_week_invoices'),
  totalSaleValue: assertNumber(raw?.total_sale_value, 'total_sale_value'),
  netPayout: assertNumber(raw?.net_payout, 'net_payout'),
  applicationFee: assertNumber(raw?.application_fee, 'application_fee'),
  kraveinFee: assertNumber(raw?.kravein_fee, 'kravein_fee'),
  deliveryFee: assertNumber(raw?.delivery_fee, 'delivery_fee'),
  kraveinCharges: assertNumber(raw?.kravein_charges, 'kravein_charges'),
  creditCardFees: assertNumber(raw?.credit_card_fees, 'credit_card_fees'),
  kraveinPayout: assertNumber(raw?.kravein_payout, 'kravein_payout'),
});

// ─── Error Normalizer ─────────────────────────────────────────────────────────

const isAppError = err =>
  typeof err === 'object' && err !== null && 'code' in err && 'message' in err;

const toAppError = err => {
  if (err instanceof Error) {
    const msg = err.message.toLowerCase();
    if (msg.includes('unauthorized') || msg.includes('unauthenticated')) {
      return { message: err.message, code: 'UNAUTHORIZED' };
    }
    if (msg.includes('network') || msg.includes('failed to fetch')) {
      return { message: err.message, code: 'NETWORK_ERROR' };
    }
    return { message: err.message, code: 'UNKNOWN_ERROR' };
  }
  return { message: 'An unexpected error occurred', code: 'UNKNOWN_ERROR' };
};

// ─── Service Implementation ───────────────────────────────────────────────────

export async function getCompanyInvoiceSummary(
  input,
  client = apolloClient.clientPrivate,
) {
  const { userId, fromDate, toDate } = input;

  try {
    const { data } = await client.query({
      query: COMPANY_INVOICE_SUMMARY,
      variables: {
        input: {
          user_id: userId,
          from_date: fromDate,
          to_date: toDate,
        },
      },
    });

    const result = data?.company_invoice_summary;

    if (!result) {
      throw {
        message: 'No data returned from company_invoice_summary',
        code: 'PARSE_ERROR',
      };
    }

    if (result.error?.length) {
      throw {
        message: result.error[0]?.description ?? 'API error',
        code: 'API_ERROR',
      };
    }

    return toCompanyInvoiceSummary(result);
  } catch (err) {
    if (isAppError(err)) throw err;
    throw toAppError(err);
  }
}

// ─── Export Pure Transformer ──────────────────────────────────────────────────

export { toCompanyInvoiceSummary };

// ─── Export Service ───────────────────────────────────────────────────────────

export const companyInvoiceSummaryService = {
  getCompanyInvoiceSummary,
};
